import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ProductCard } from "../../../products/presentation/components/product-card";
import { useWishlistStore } from "../../../products/domain/stores/useWishlistStore";
import {
  computeDiscount,
  getStockLabel,
  getWishlistVariantId,
} from "../../../products/utils/card-props";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";
import type { ProductDto } from "../../../../api";
import { useFeaturedDeals } from "../../hooks/useFeaturedDeals";

interface Props {
  /** Visible cards. Default 4 to match the catalog's 4-col row. */
  limit?: number;
  /** Label for the "Explore" CTA button. */
  ctaLabel?: string;
  /** Where the CTA goes. Defaults to the catalog filtered to Deal of the Week. */
  ctaTarget?: string;
}

/**
 * Public-facing "Deal of the Week" row used on the landing page and the
 * AI search page. Uses a flex layout (not the catalog's CSS grid) so that
 * a partial row of N items (N < 4) stays centered horizontally instead of
 * collapsing to the left edge.
 */
export function HandpickedDealsSection({
  limit = 4,
  ctaLabel = "Explore Our Deals",
  ctaTarget = `${AppRoutes.PRODUCTS}?dealOfWeek=1`,
}: Props) {
  const navigate = useNavigate();
  const wishlistIds = useWishlistStore((s) => s.wishlistVariantIds);
  const toggleWishlist = useWishlistStore((s) => s.toggle);

  const { data: featured, isLoading } = useFeaturedDeals();
  const products = useMemo<ProductDto[]>(
    () =>
      (featured ?? [])
        .map((d) => d.product as unknown as ProductDto | null)
        .filter((p): p is ProductDto => p != null)
        .slice(0, limit),
    [featured, limit],
  );

  const cardPropsGetter = useCallback((product: ProductDto) => {
    const discount = computeDiscount(product);
    const variantId = getWishlistVariantId(product);
    return {
      discountLabel: discount ? `${discount}% off` : undefined,
      originalPrice: discount ? (product.standardPrice ?? undefined) : undefined,
      stockLabel: getStockLabel(product),
      isInWishlist: variantId != null && wishlistIds.includes(variantId),
      onToggleWishlist:
        variantId != null ? () => void toggleWishlist(variantId) : undefined,
    };
  }, [wishlistIds, toggleWishlist]);

  if (!isLoading && products.length === 0) return null;

  const openDetails = (product: ProductDto) => {
    navigate(getProductDetailsUrl(product.id, product.variantId ?? null));
  };

  return (
    <section
      // `!` (important) modifiers override the .landing-page-scoped CSS reset
      // (`.landing-page * { margin: 0; padding: 0; }`) which would otherwise
      // strip mx-auto / px-8 / py-10 on this surface.
      className="relative z-[2] !mx-auto flex w-full max-w-[1440px] flex-col items-center gap-6 !px-8 !py-10"
      data-reveal
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <Text variant="bodyLg" tone="body">
          Handpicked offers from verified suppliers — updated daily
        </Text>
      </div>

      {/* Flex (not grid) so a partial row of N < 4 cards stays centered. */}
      <div className="flex w-full flex-wrap justify-center gap-6">
        {products.map((product) => (
          <div
            key={product.id}
            onClick={() => openDetails(product)}
            className="w-full cursor-pointer sm:w-[320px] lg:w-[340px] xl:w-[320px]"
          >
            <ProductCard
              product={product}
              onViewDetails={() => openDetails(product)}
              {...cardPropsGetter(product)}
            />
          </div>
        ))}
      </div>

      <Button variant="primary" onClick={() => navigate(ctaTarget)}>
        {ctaLabel}
      </Button>
    </section>
  );
}
